import React, { useState, useRef, useEffect, useMemo } from "react";

/**
 * `groups`: optional pinned rows `[{ label, items }]` shown above the option list.
 * Checking a group selects every one of its items that exists in `options`
 * (unchecking removes them); the button reads the group name when exactly that
 * set is selected. Selection stays a plain list of option values.
 */
export default function MultiSelectDropdown({
  label,
  options = [],
  selected = [],
  onChange,
  placeholder = "All",
  icon = null,
  compact = false,
  groups = [],
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (item) => {
    if (selected.includes(item)) {
      onChange(selected.filter((s) => s !== item));
    } else {
      onChange([...selected, item]);
    }
  };

  const selectAll = () => onChange([...options]);
  const clearAll = () => onChange([]);

  const groupRows = useMemo(
    () =>
      (groups || [])
        .map((g) => {
          const items = (g.items || []).filter((i) => options.includes(i));
          const checked = items.filter((i) => selected.includes(i)).length;
          return { label: g.label, items, all: items.length > 0 && checked === items.length, some: checked > 0 };
        })
        .filter((g) => g.items.length > 0),
    [groups, options, selected]
  );

  const toggleGroup = (g) => {
    if (g.all) {
      onChange(selected.filter((s) => !g.items.includes(s)));
    } else {
      onChange([...new Set([...selected, ...g.items])]);
    }
  };

  const hasPartialSelection =
    selected.length > 0 && selected.length < options.length;

  const exactGroup = groupRows.find((g) => g.all && g.items.length === selected.length);

  const displayText =
    selected.length === 0 || selected.length === options.length
      ? placeholder
      : exactGroup
        ? exactGroup.label
        : selected.length === 1
          ? selected[0]
          : `${selected.length} selected`;

  const buttonClass = compact
    ? `flex items-center gap-1.5 px-3 py-2 text-body-xs font-medium rounded-lg border transition-all ${
        hasPartialSelection
          ? "border-accent-purple bg-accent-purple/5 text-accent-purple"
          : "border-border-subtle text-text-secondary hover:border-border-default"
      }`
    : "flex items-center justify-between gap-2 w-full px-3 py-2 text-body-sm bg-white border border-border-subtle rounded-xl hover:border-accent-purple/40 focus:outline-none focus:ring-2 focus:ring-accent-purple/20 transition-all min-w-[140px]";

  return (
    <div className="relative" ref={ref}>
      {label && <label className="block text-body-xs font-medium text-text-secondary mb-1">{label}</label>}
      <button
        type="button"
        onClick={() => { setIsOpen(!isOpen); setSearch(""); }}
        className={buttonClass}
      >
        {icon}
        <span className={`truncate ${hasPartialSelection ? "text-inherit" : compact ? "" : "text-text-tertiary"}`}>
          {displayText}
        </span>
        <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""} ${compact ? "" : "w-3.5 h-3.5 text-text-tertiary"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-64 bg-white border border-border-subtle rounded-xl shadow-lg overflow-hidden animate-fade-in">
          <div className="p-2 border-b border-border-subtle">
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-tertiary pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-1.5 text-body-xs bg-surface-neutral rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-accent-purple/20 placeholder:text-text-tertiary"
              />
            </div>
          </div>

          <div className="px-2 py-1.5 border-b border-border-subtle flex items-center gap-2">
            <button type="button" onClick={selectAll} className="text-caption font-medium text-accent-purple hover:underline">All</button>
            <span className="text-text-tertiary text-caption">·</span>
            <button type="button" onClick={clearAll} className="text-caption font-medium text-text-tertiary hover:text-text-secondary">None</button>
            <span className="ml-auto text-caption text-text-tertiary">{selected.length}/{options.length}</span>
          </div>

          {groupRows.length > 0 && (
            <div className="p-1 border-b border-border-subtle">
              {groupRows.map((g) => (
                <label
                  key={g.label}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
                    g.all ? "bg-accent-purple/5" : "hover:bg-surface-neutral"
                  }`}
                  title={g.items.join(", ")}
                >
                  <input
                    type="checkbox"
                    checked={g.all}
                    ref={(el) => {
                      if (el) el.indeterminate = g.some && !g.all;
                    }}
                    onChange={() => toggleGroup(g)}
                    className="h-3.5 w-3.5 text-accent-purple border-border-default rounded focus:ring-accent-purple/30 cursor-pointer"
                  />
                  <span className="text-body-xs font-semibold text-text-primary truncate">{g.label}</span>
                  <span className="ml-auto text-caption text-text-tertiary">{g.items.length}</span>
                </label>
              ))}
            </div>
          )}

          <div className="max-h-48 overflow-y-auto p-1 scrollbar-thin">
            {filtered.length === 0 ? (
              <p className="text-body-xs text-text-tertiary text-center py-3">No results</p>
            ) : (
              filtered.map((item) => {
                const isChecked = selected.includes(item);
                return (
                  <label
                    key={item}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
                      isChecked ? "bg-accent-purple/5" : "hover:bg-surface-neutral"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(item)}
                      className="h-3.5 w-3.5 text-accent-purple border-border-default rounded focus:ring-accent-purple/30 cursor-pointer"
                    />
                    <span className="text-body-xs text-text-primary truncate">{item}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
